import { Button } from 'components/Button'
import { useIsMobile } from 'hooks'
import { MobilePageHeader } from 'modules/creative/components/MobilePageHeader'
import { CHANNEL_META } from 'components/ChannelBadge'
import { Eyebrow } from 'components/Eyebrow'
import { FacebookIcon } from 'components/FacebookIcon'
import { Heading } from 'components/Heading'
import { platformLabel } from 'components/PlatformIcon'
import { Text } from 'components/Text'
import { selectBusinesses } from 'modules/business'
import { useAppSelector } from 'modules/core'
import {
  Check,
  CirclePlus,
  Copy,
  Gift,
  Linkedin,
  Mail,
  Share,
  Share2,
  UserRound,
} from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { Fragment, useState } from 'react'
import { ReferralClaimDialog } from './ReferralClaimDialog'
import { canNativeShare, useReferralShare, type ReferralShareChannel } from './use-referral-share'
import { useReferralProgram } from './use-referral-program'

const SHARE_CHANNELS: ReferralShareChannel[] = ['email', 'facebook', 'x', 'linkedin']

/** The X wordmark glyph — lucide has no X logo and the badge SVG is a tile. */
const XGlyph = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
)

/** Brand-tinted glyph for a share tile. */
const ShareGlyph = ({ channel }: { channel: ReferralShareChannel }) => {
  switch (channel) {
    case 'email':
      return <Mail size={18} />
    case 'facebook':
      return (
        <span className="flex" style={{ color: CHANNEL_META.facebook.color }}>
          <FacebookIcon size={18} />
        </span>
      )
    case 'x':
      return (
        <span className="flex text-paper">
          <XGlyph size={15} />
        </span>
      )
    case 'linkedin':
      return <Linkedin size={18} style={{ color: CHANNEL_META.linkedin.color }} />
  }
}

/**
 * Refer-a-friend settings page. Renders nothing (redirects to profile) when the
 * `referral_program` gate is off OR the server reports the program disabled —
 * the sidebar entry is hidden under the same condition, so this is a
 * belt-and-suspenders guard for direct deep-links.
 */
export const ReferralPage = () => {
  const { enabled, info, loading } = useReferralProgram()
  const { copied, copyLink, nativeShare, shareVia } = useReferralShare(
    'settings',
    info?.refereeCredits
  )
  const businesses = useAppSelector(selectBusinesses)
  // Claiming happens inside the shared dialog; this page only opens it.
  const [claimDialogOpen, setClaimDialogOpen] = useState(false)
  const isMobile = useIsMobile()

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-acid" />
      </div>
    )
  }

  // Disabled cohort or missing code — nothing to show. Bounce to profile so a
  // stale deep-link doesn't dead-end on a blank page.
  if (!enabled || !info || !info.code || !info.link) {
    return <Navigate to="/profile" replace />
  }

  const {
    link,
    pendingCount,
    rewardedCount,
    creditsEarned,
    refereeCredits,
    referrerCredits,
    claimable,
  } = info
  const friendsJoined = pendingCount + rewardedCount
  const showNativeShare = canNativeShare()
  const claimableTotal = claimable.reduce((sum, r) => sum + r.credits, 0)

  const handleClaimClick = () => {
    if (claimable.length === 0 || businesses.length === 0) return
    // Always open the shared dialog — it adapts to the business count (a pure
    // celebration modal for one business, a picker for 2+). Zero-biz is
    // impossible here (the sidebar entry is gated on an active business), but
    // guarded defensively anyway.
    setClaimDialogOpen(true)
  }

  const steps = [
    { Icon: Share, label: 'Share link', acid: true },
    { Icon: UserRound, label: 'They join', acid: false },
    { Icon: CirclePlus, label: `You earn ${referrerCredits}`, acid: false },
  ]

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* Instant header on mobile — the route rendered headerless at 390
          until content landed (monkey finding). */}
      {isMobile && (
        <MobilePageHeader title="Refer a friend" subtitle="Earn credits when friends subscribe" />
      )}
      <div
        className={`mx-auto w-full max-w-[720px] space-y-4 ${isMobile ? 'px-4 py-5' : 'px-6 py-10'}`}
      >
        {/* Approved-but-unclaimed banner — pinned above the hero so it's the
            first thing the user sees on the page. Kept out of the celebration
            flow so the user can revisit later even after dismissing the
            auto-popped dialog. */}
        {claimable.length > 0 && (
          <div className="relative overflow-hidden rounded-[12px] border border-acid bg-bg-2 p-5 sm:p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-acid bg-bg-3 text-acid">
                <Gift size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[16px] leading-tight font-semibold text-paper">
                  <span className="text-acid">{claimableTotal} credits</span> waiting to be claimed
                </div>
                <Text as="p" variant="body-sm" tone="paper-dim" className="mt-1">
                  {claimable.length === 1
                    ? 'A friend you invited subscribed.'
                    : `${claimable.length} friends you invited subscribed.`}
                </Text>
              </div>
              <Button type="button" variant="t3-primary" size="t3-md" onClick={handleClaimClick}>
                Claim
              </Button>
            </div>
          </div>
        )}

        <ReferralClaimDialog
          open={claimDialogOpen}
          onClose={() => setClaimDialogOpen(false)}
          rewards={claimable}
        />

        {/* Hero — the offer and the primary share action */}
        <div className="card relative overflow-hidden p-7 sm:p-8">
          <div className="grid-bg absolute inset-0 opacity-60" aria-hidden />
          <div
            className="glow"
            aria-hidden
            style={{ top: -160, right: -140, width: 520, height: 420 }}
          />
          <div className="relative">
            <Eyebrow>Refer a friend</Eyebrow>
            <Heading level="display" className="mt-3">
              Give {refereeCredits}, <span className="text-acid">get {referrerCredits}</span>{' '}
              credits.
            </Heading>
            <Text as="p" variant="body" tone="paper-dim" className="mt-3 max-w-[52ch]">
              Share your link. Your friend gets {refereeCredits} credits to start. You earn{' '}
              {referrerCredits} when they subscribe. Credits never expire.
            </Text>

            {/* How it works — three steps joined by hairlines */}
            <div className="mt-8 flex items-start">
              {steps.map(({ Icon, label, acid }, index) => (
                <Fragment key={label}>
                  {index > 0 && <div className="mx-3 mt-6 h-px flex-1 bg-line" />}
                  <div className="flex flex-col items-center gap-2.5">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-full border border-line bg-bg-2 ${
                        acid ? 'text-acid' : 'text-paper-dim'
                      }`}
                    >
                      <Icon size={18} />
                    </div>
                    <span className="mono text-[12px] text-paper-dim">{label}</span>
                  </div>
                </Fragment>
              ))}
            </div>

            {/* Link + copy */}
            <div className="mt-8 space-y-2">
              <Eyebrow>Your link</Eyebrow>
              <div className="flex items-center gap-3">
                <div
                  className="mono min-w-0 flex-1 truncate rounded-[10px] border border-line bg-bg-3 px-4 py-3.5 text-[13.5px] text-paper-dim"
                  title={link}
                >
                  {link}
                </div>
                <Button
                  type="button"
                  variant="t3-primary"
                  size="t3-lg"
                  onClick={() => copyLink(link)}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
            </div>

            {/* Direct share intents — brand glyphs open a prefilled share dialog */}
            <div className="mt-5 flex items-center gap-2.5">
              <span className="mono text-[12.5px] text-paper-dim">Or share on</span>
              {SHARE_CHANNELS.map(channel => (
                <Button
                  key={channel}
                  type="button"
                  variant="t3-tertiary"
                  size="icon"
                  className="rounded-[12px]"
                  aria-label={`Share on ${platformLabel(channel)}`}
                  onClick={() => shareVia(channel, link)}
                >
                  <ShareGlyph channel={channel} />
                </Button>
              ))}
              {showNativeShare && (
                <Button
                  type="button"
                  variant="t3-tertiary"
                  size="icon"
                  className="rounded-[12px]"
                  aria-label="More ways to share"
                  onClick={() => nativeShare(link)}
                >
                  <Share2 size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="card p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Heading level="h3">Your rewards</Heading>
              <Text as="p" variant="body-sm" tone="paper-dim" className="mt-0.5">
                {friendsJoined === 0
                  ? 'No referrals yet. Share your link to get started.'
                  : `${friendsJoined} ${friendsJoined === 1 ? 'friend has' : 'friends have'} joined${
                      pendingCount > 0 ? `, ${pendingCount} still to convert` : ''
                    }.`}
              </Text>
            </div>
            <div className="flex items-center gap-6 text-right">
              <div>
                <div className="text-[26px] font-semibold tracking-[-0.01em] text-paper">
                  {friendsJoined}
                </div>
                <Eyebrow>Joined</Eyebrow>
              </div>
              <div className="h-10 w-px bg-line" />
              <div>
                <div className="text-[26px] font-semibold tracking-[-0.01em] text-acid">
                  {creditsEarned}
                </div>
                <Eyebrow>Earned</Eyebrow>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
